import { inject, Injectable } from '@angular/core';
import { filter, map, of, switchMap } from 'rxjs';
import { Option } from '@sapphire/result';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import {
  ImpersonationActions,
  selectHasImpersonatedBuyer,
  selectImpersonatedBuyer,
  selectUser,
} from '@ps/auth-data-access';
import { markCached } from '@ps/utils';
import { ToastService } from '@ps/core-overlay';

import { TemplateService } from '../services/template.service';
import { TEMPLATE_CACHE_TTL_MS } from '../utils/template-cache.constants';
import { TemplateActions, TemplateApiActions } from './template.actions';
import { selectTemplatesCachedAt } from './template.selectors';

@Injectable()
export class TemplateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _templateService = inject(TemplateService);
  private readonly _toast = inject(ToastService);

  public readonly loadTemplates$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.loadTemplates),
      withCacheGate(
        this._store.select(selectTemplatesCachedAt),
        TEMPLATE_CACHE_TTL_MS,
      ),
      switchMap(({ hasImpersonatedBuyer, companyId }) =>
        this._templateService
          .getTemplates({ hasImpersonatedBuyer, companyId })
          .pipe(
            map((templates) =>
              TemplateApiActions.loadTemplatesSuccess({
                templates,
                cachedAt: markCached(),
              }),
            ),
            this.psCatchError((err) =>
              of(
                TemplateApiActions.loadTemplatesFailure({
                  error: err.message || 'Failed to load templates',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  /**
   * Only reloads when the operator has impersonated a buyer — matches
   * `reloadOnImpersonationContextSettled$` in `@ps/survey-data-access`.
   * The reducer resets to the initial list fields on the same action,
   * so this dispatch always passes the cache gate above.
   */
  public readonly reloadOnImpersonationContextSettled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ImpersonationActions.contextSettled),
      filter(({ hasImpersonatedBuyer }) => hasImpersonatedBuyer),
      map(({ companyId }) =>
        TemplateActions.loadTemplates({
          hasImpersonatedBuyer: true,
          companyId,
        }),
      ),
    ),
  );

  public readonly createTemplateFolder$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TemplateActions.createTemplateFolder),
      concatLatestFrom(() => this._store.select(selectHasImpersonatedBuyer)),
      switchMap(([{ name, parentFolderId }, hasImpersonatedBuyer]) =>
        this._templateService
          .createTemplateFolder(name, hasImpersonatedBuyer, parentFolderId)
          .pipe(
            map((folder) =>
              TemplateApiActions.createTemplateFolderSuccess({ folder }),
            ),
            this.psCatchError((err) => {
              this._toast.error(err.message, { keepOpen: false });
              const message = err.message || 'Failed to create template folder';
              return of(
                TemplateApiActions.createTemplateFolderFailure({
                  error: message,
                }),
              );
            }),
          ),
      ),
    ),
  );

  public readonly reloadTemplatesAfterListChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TemplateApiActions.createTemplateFolderSuccess,
        TemplateApiActions.saveTemplateSuccess,
        TemplateApiActions.updateTemplateSuccess,
      ),
      concatLatestFrom(() => [
        this._store.select(selectHasImpersonatedBuyer),
        this._store.select(selectImpersonatedBuyer),
        this._store.select(selectUser),
      ]),
      map(([, hasImpersonatedBuyer, impersonatedBuyer, user]) =>
        TemplateActions.loadTemplates({
          hasImpersonatedBuyer,
          companyId:
            impersonatedBuyer?.id ?? Option.from(user).unwrap().companyId,
        }),
      ),
    ),
  );
}
